import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { NotebookPen, Trash2, ChevronDown, ChevronUp, ArrowUpRight, ArrowDownRight, Sparkles, Crosshair, BellRing, PenLine, ArrowLeftRight } from "lucide-react";
import { api, fmtPct, fmtUsd } from "@/lib/api";
import { Hint } from "@/components/Hint";
import { Locked } from "@/components/Locked";
import { PaperScorecard } from "@/components/trade/PaperScorecard";

export const TYPE_META = {
  trade_buy: { label: "Paper buy", icon: ArrowUpRight, cls: "text-emerald-300 bg-emerald-500/10 border-emerald-900/60" },
  trade_sell: { label: "Paper sell", icon: ArrowDownRight, cls: "text-red-300 bg-red-500/10 border-red-900/60" },
  paper_trade_closed: { label: "Paper trade closed", icon: ArrowLeftRight, cls: "text-amber-300 bg-amber-500/10 border-amber-900/60" },
  signal: { label: "AI signal", icon: Sparkles, cls: "text-cyan-300 bg-cyan-500/10 border-cyan-900/60" },
  bracket_armed: { label: "Bracket armed", icon: Crosshair, cls: "text-amber-300 bg-amber-500/10 border-amber-900/60" },
  bracket_fired: { label: "Bracket fired", icon: BellRing, cls: "text-violet-300 bg-violet-500/10 border-violet-900/60" },
  note: { label: "Note", icon: PenLine, cls: "text-slate-300 bg-slate-800/60 border-slate-700" },
};
const FILTERS = [["", "All"], ["trade_buy,trade_sell,paper_trade_closed", "Trades"], ["signal", "AI signals"], ["bracket_armed,bracket_fired", "Brackets"], ["note", "Notes"]];
const rel = (iso) => { const s = (Date.now() - new Date(iso).getTime()) / 1000; if (s < 60) return "just now"; if (s < 3600) return `${Math.floor(s / 60)}m ago`; if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return `${Math.floor(s / 86400)}d ago`; };
const num = (v, d = 2) => (v == null ? "—" : Number(v).toFixed(d));

export default function Journal() {
  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      <Locked feature="journal" title="Decision Journal is a Pro feature"
        desc="Every paper trade, AI analysis, armed bracket and note is recorded live with the indicators at that moment. Entries keep accruing while you are on Free and unlock on Pro.">
        <JournalInner />
      </Locked>
    </div>
  );
}

function JournalInner() {
  const [data, setData] = useState({ entries: [], counts: {} });
  const [type, setType] = useState("");
  const [coin, setCoin] = useState("");
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/journal", { params: { type: type || undefined, coin_id: coin || undefined, limit: 200 } })
      .then((r) => setData(r.data)).catch((e) => console.warn("[apex] journal:", e?.message || e)).finally(() => setLoading(false));
  };
  useEffect(load, [type, coin]);

  const coins = useMemo(() => Array.from(new Map(data.entries.map((e) => [e.coin_id, e.symbol])).entries()), [data.entries]);
  const total = Object.values(data.counts || {}).reduce((a, b) => a + b, 0);
  const remove = async (id) => { await api.delete(`/journal/${id}`); load(); };

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-6" data-testid="journal-page">
      <div className="flex items-start gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold flex items-center gap-2"><NotebookPen className="w-7 h-7 text-cyan-300" /> Decision Journal</h1>
          <p className="text-sm text-slate-400 mt-1">Every paper trade, AI analysis, armed bracket and note — recorded live with the indicators at that moment, so you can audit your own reasoning over time.</p>
        </div>
        <div className="ml-auto grid grid-cols-3 gap-2 text-center">
          <Kpi label="Entries" value={total} tid="journal-total" />
          <Kpi label="Trades" value={(data.counts.trade_buy || 0) + (data.counts.trade_sell || 0) + (data.counts.paper_trade_closed || 0)} />
          <Kpi label="AI signals" value={data.counts.signal || 0} />
        </div>
      </div>

      <PaperScorecard />

      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex gap-1 bg-slate-900 rounded-lg p-1 border border-slate-800">
          {FILTERS.map(([v, l]) => (
            <button key={l} data-testid={`journal-filter-${l.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setType(v)}
              className={`px-3 py-1 rounded-md text-xs font-mono-ticker ${type === v ? "bg-cyan-500/15 text-cyan-300" : "text-slate-400 hover:text-slate-200"}`}>{l}</button>
          ))}
        </div>
        <select data-testid="journal-coin-filter" value={coin} onChange={(e) => setCoin(e.target.value)} className="apx-ring px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-800 outline-none text-xs">
          <option value="">All coins</option>
          {coins.map(([id, sym]) => <option key={id} value={id}>{(sym || id).toUpperCase()}</option>)}
        </select>
      </div>

      <div className="apx-card">
        {loading && !data.entries.length ? <div className="p-8 skeleton h-24" /> : data.entries.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="journal-empty">
            Nothing logged yet. Place a paper trade, run an AI analysis, arm a bracket or add a note on any <Link className="text-cyan-300 underline" to="/dashboard">coin page</Link>.
          </div>
        ) : (
          <div className="divide-y divide-slate-900" data-testid="journal-list">
            {data.entries.map((e) => <Entry key={e.id} e={e} open={open === e.id} onToggle={() => setOpen(open === e.id ? null : e.id)} onDelete={() => remove(e.id)} />)}
          </div>
        )}
      </div>
      <div className="text-[11px] text-slate-500 apx-card p-4">Snapshots are frozen at the moment of each decision and never rewritten. "Since" compares today's price with the price at that moment — it is an outcome, not a prediction.</div>
    </div>
  );
}

const Kpi = ({ label, value, tid }) => (
  <div className="apx-card px-3 py-2 min-w-[84px]"><div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">{label}</div><div className="font-mono-ticker font-bold" data-testid={tid}>{value}</div></div>
);

const Entry = ({ e, open, onToggle, onDelete }) => {
  const m = TYPE_META[e.type] || TYPE_META.note; const Icon = m.icon; const s = e.snapshot || {};
  const up = (e.move_since_pct ?? 0) >= 0;
  return (
    <div data-testid={`journal-entry-${e.id}`} className="px-4 py-3">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${m.cls}`}><Icon className="w-4 h-4" /></div>
        <div className="min-w-0 flex-1">
          <div className="text-sm flex items-center gap-2 flex-wrap">
            <Link to={`/coin/${e.coin_id}`} className="font-semibold hover:text-cyan-300">{(e.symbol || e.coin_id).toUpperCase()}</Link>
            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${m.cls}`}>{m.label}</span>
            <span className="text-xs text-slate-400 truncate">{e.note}</span>
          </div>
          <div className="text-[11px] text-slate-500 font-mono-ticker mt-0.5 flex gap-3 flex-wrap">
            <span>{rel(e.created_at)} · {new Date(e.created_at).toLocaleString()}</span>
            {e.price != null && <span>@ {fmtUsd(e.price)}</span>}
            {e.type === "paper_trade_closed" && s.pnl_usd != null && <span className={`font-semibold ${Number(s.pnl_usd) >= 0 ? "text-emerald-300" : "text-red-300"}`} data-testid={`journal-pnl-${e.id}`}>P&L {Number(s.pnl_usd) >= 0 ? "+" : "−"}${Math.abs(Number(s.pnl_usd)).toFixed(2)} ({Number(s.pnl_pct) >= 0 ? "+" : ""}{Number(s.pnl_pct).toFixed(2)}%) · {s.exit_fired}</span>}
            {s.rsi != null && <span>RSI {num(s.rsi, 1)}</span>}
            {s.ai_outlook && <span>AI {s.ai_outlook}{s.ai_strength != null ? ` ${s.ai_strength}` : ""}</span>}
          </div>
        </div>
        {e.move_since_pct != null && (
          <Hint title="Since this decision" text="Price move from the moment of this entry to now." asChild>
            <span className={`text-xs font-mono-ticker font-semibold cursor-help ${up ? "text-emerald-300" : "text-red-300"}`} data-testid={`journal-move-${e.id}`}>{fmtPct(e.move_since_pct)}</span>
          </Hint>
        )}
        <button onClick={onToggle} className="btn-ghost rounded-md p-2" data-testid={`journal-toggle-${e.id}`} aria-label="Toggle snapshot">{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</button>
        <button onClick={onDelete} className="btn-ghost rounded-md p-2" data-testid={`journal-delete-${e.id}`} aria-label="Delete entry"><Trash2 className="w-4 h-4 text-slate-500" /></button>
      </div>
      {open && (
        <div className="mt-3 ml-11 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-xs font-mono-ticker" data-testid={`journal-snapshot-${e.id}`}>
          {Object.entries(s).filter(([, v]) => v != null && typeof v !== "object").map(([k, v]) => (
            <div key={k}><div className="text-[10px] uppercase tracking-widest text-slate-500">{k.replace(/_/g, " ")}</div><div className="text-slate-200">{typeof v === "number" ? (Math.abs(v) >= 1000 ? fmtUsd(v) : num(v, Math.abs(v) < 1 ? 3 : 2)) : String(v)}</div></div>
          ))}
          {!Object.keys(s).length && <div className="text-slate-500 col-span-full">No indicator snapshot was available at that moment.</div>}
        </div>
      )}
    </div>
  );
};
